/**
 * Environment-specific theming utilities for visual distinction across environments.
 * 
 * These helpers provide consistent visual cues (background colors, banner styles)
 * that make it immediately obvious which environment someone is on when viewing
 * screenshots or during support debugging.
 * 
 * Environment color scheme:
 * - Production: Dark purple (brand color)
 * - Staging: Dark blue (testing/training environment)
 * - Development: Dark amber/yellow (dev environment)
 */

/**
 * Returns an environment-specific CSS gradient string for page backgrounds.
 * Used on onboarding and authentication pages to visually distinguish environments.
 * 
 * @returns {string} CSS linear-gradient value
 */
export const getBackgroundGradient = () => {
  if (import.meta.env.VITE_ENVIRONMENT === "development") {
    // Dark amber/yellow gradient for development
    return 'linear-gradient(135deg, #78350f 0%, #92400e 50%, #451a03 100%)';
  }
  if (import.meta.env.VITE_ENVIRONMENT === "staging") {
    // Dark blue gradient for staging/testing
    return 'linear-gradient(135deg, #0f172a 0%, #1e3a8a 50%, #0c1a3d 100%)';
  }
  // Default: Dark purple gradient for production (brand color)
  return 'linear-gradient(135deg, #43213e 0%, #5a2a53 50%, #2a1326 100%)';
};

/**
 * True when the app is running in a non-production environment (development or
 * staging), as set by the VITE_ENVIRONMENT build variable. Production builds
 * (portal, SLaM NHS) leave this false.
 *
 * Use this to gate testing-only affordances that must NEVER reach production —
 * e.g. the "Reverse" SSI-takeover button on the Patient Overview, which lets
 * testers undo a Lead takeover so they can re-run the flow on the same patient.
 *
 * @returns {boolean} True in development or staging only
 */
export const isNonProdEnvironment = () => {
  return (
    import.meta.env.VITE_ENVIRONMENT === "development" ||
    import.meta.env.VITE_ENVIRONMENT === "staging"
  );
};

/**
 * Determines whether to show the environment banner (dev/staging only).
 * Production environments do not show a banner.
 * 
 * @returns {boolean} True if environment banner should be displayed
 */
export const shouldShowEnvBanner = () => {
  return isNonProdEnvironment();
};

/**
 * Returns the appropriate CSS classes for the environment banner.
 * Yellow for development, blue for staging.
 * 
 * @returns {string} Tailwind CSS classes for the banner
 */
export const getEnvBannerClasses = () => {
  if (import.meta.env.VITE_ENVIRONMENT === "development") {
    return "bg-yellow-200 text-yellow-900 border-yellow-300";
  }
  return "bg-blue-200 text-blue-900 border-blue-300";
};

/**
 * Returns the display text for the environment banner.
 * 
 * @returns {string} Human-readable environment name
 */
export const getEnvBannerText = () => {
  if (import.meta.env.VITE_ENVIRONMENT === "development") {
    return "Development Environment";
  }
  return "Testing / Training Environment";
};
